import { useEffect, useMemo, useRef, useState } from 'react';
import { getAssignableUsers, type Assignee } from '../data/assignees';
import { textMatch } from './tableTools';
import { useStoreAccountLabel } from './PrincipalField';
import { useFloatingList } from './useFloatingList';

/**
 * Assignee combobox: like PrincipalField in look/feel, but restricted to
 * ACTUAL APP USERS (data/assignees.ts) — an assignee must be picked from
 * the list, free text never becomes a value (owner ruling 2026-09-16:
 * non-users would get task emails whose link they can't open). Use for
 * task assignment only; notify/CC pickers stay full-directory.
 */
export function AssigneeField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  /** Selected UPN ('' = none). */
  value: string;
  onChange: (upn: string) => void;
  required?: boolean;
}) {
  const [people, setPeople] = useState<Assignee[] | null>(null);
  const [unexpanded, setUnexpanded] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(false);
  // Only filter once the user types after opening (see StoreSearchSelect).
  const [typed, setTyped] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listStyle = useFloatingList(inputRef, open);

  useEffect(() => {
    let cancelled = false;
    getAssignableUsers()
      .then((result) => {
        if (cancelled) return;
        setPeople(result.people);
        setUnexpanded(result.unexpandedGroups);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = (people ?? []).find((p) =>
    p.upn.toLowerCase() === value.trim().toLowerCase(),
  );
  // A value not in the list still displays: as its store when it's a
  // store account, else as the raw address.
  const storeLabel = useStoreAccountLabel(selected ? '' : value);
  const text = typed !== null ? typed : selected ? selected.name : (storeLabel ?? value);

  const shown = useMemo(() => {
    const q = (typed ?? '').trim();
    if (people === null) return [];
    return q
      ? people.filter((p) => textMatch(q, p.name, p.upn))
      : people;
  }, [people, typed]);

  const pick = (p: Assignee) => {
    onChange(p.upn);
    setTyped(null);
    setOpen(false);
    inputRef.current?.blur();
  };

  const close = () => {
    setOpen(false);
    setTyped(null); // revert text to the selection
  };

  return (
    <label className="typeahead">
      {label}
      <input
        ref={inputRef}
        value={text}
        required={required ?? false}
        placeholder="Type to search app users…"
        autoComplete="off"
        onFocus={() => {
          setOpen(true);
          setTyped(null);
          setTimeout(() => inputRef.current?.select(), 0);
        }}
        onBlur={() => setTimeout(close, 150)}
        onChange={(e) => {
          const raw = e.target.value;
          setTyped(raw);
          setOpen(true);
          if (raw.trim() === '' && value !== '') onChange('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && open) {
            e.preventDefault();
            if (shown.length > 0 && typed !== null && typed.trim() !== '') {
              pick(shown[0]!);
            } else {
              close();
            }
          } else if (e.key === 'Escape') {
            // Dismiss the suggestions only — a dialog around the field must not close too (QA 2026-10-26).
            e.stopPropagation();
            close();
          }
        }}
      />
      {loadError && (
        <div className="typeahead-status error">
          Could not load the user list — reload the page to retry.
        </div>
      )}
      {unexpanded > 0 && (
        <div className="typeahead-status error">
          {unexpanded} role group{unexpanded > 1 ? 's' : ''} could not be
          expanded — people whose access comes from{' '}
          {unexpanded > 1 ? 'those groups' : 'that group'} are missing from
          this list.
        </div>
      )}
      {open && !loadError && (
        <div className="typeahead-results" style={listStyle}>
          {people === null && (
            <div className="typeahead-status muted">Loading users…</div>
          )}
          {people !== null &&
            shown.map((p) => (
              <button
                type="button"
                className="typeahead-result"
                key={p.upn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(p)}
              >
                <span>
                  {p.kind === 'store' ? `${p.name} (store account)` : p.name}
                  {value && p.upn.toLowerCase() === value.trim().toLowerCase() && ' ✓'}
                </span>
                <span className="muted mono">{p.upn}</span>
              </button>
            ))}
          {people !== null && shown.length === 0 && (
            <div className="typeahead-status muted">
              No app users match. Assignees must be app users with a company
              address (@selfstorageplus.com or @arc.land) — add them in Admin →
              Users &amp; roles first.
            </div>
          )}
        </div>
      )}
    </label>
  );
}
